import { api } from "@/utils/api";

export function getPersonal() {
  try {
    return JSON.parse(localStorage.getItem("personal") || "null");
  } catch (e) {
    return null;
  }
}

export function savePersonal(patch) {
  const current = getPersonal() || {};
  const next = { ...current, ...patch };
  localStorage.setItem("personal", JSON.stringify(next));
  return next;
}

export function buildClientLogoUrl(filename) {
  const raw = String(filename || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const apiBase = process.env.VUE_APP_API_BASE_URL || process.env.VUE_APP_API_BASE_URL || "";
  const origin = String(apiBase).replace(/\/api\/?$/i, "");
  return `${origin}/public/img/logo-client/${raw.replace(/^\/+/, "")}`;
}

function clientFromPayload(data, fallback = {}) {
  const payload = data && data.data && typeof data.data === "object" && !Array.isArray(data.data)
    ? data.data
    : data || {};
  const client = payload.client && typeof payload.client === "object" ? payload.client : payload;
  const clientId = client.id_client ?? payload.id_client ?? fallback.clientId ?? null;
  const clientName = client.client_name || client.clientName || fallback.clientName || "";
  const logo = buildClientLogoUrl(client.logo || payload.logo || fallback.logo || "");
  return {
    clientId: clientId != null && clientId !== "" ? Number(clientId) : fallback.clientId ?? null,
    clientName,
    logo,
  };
}

/**
 * Ambil nama + logo client dari BE lalu simpan ke personal di localStorage.
 * force=true dipakai saat entry/login agar data cafe baru menimpa yang lama.
 */
export async function ensureClientProfile({ force = false } = {}) {
  const personal = getPersonal();
  const token = personal?.clientToken;
  if (!token) return personal;

  if (!force && personal.clientName && personal.logo) {
    return personal;
  }

  try {
    const res = await api(`/clients/${encodeURIComponent(token)}`, { method: "GET" });
    const profile = clientFromPayload(res.data, personal);
    if (profile.clientName || profile.logo) {
      return savePersonal(profile);
    }
  } catch (e) {
    // Endpoint /clients mungkin belum ada di BE production; fallback ke detail produk.
  }

  try {
    const catRes = await api(`/categories/${encodeURIComponent(token)}`, { method: "GET" });
    const list = Array.isArray(catRes.data) ? catRes.data : catRes.data?.data || [];
    const clientId = list.find((c) => c && c.client_id != null)?.client_id;
    if (clientId == null) return personal;

    const prodRes = await api(`/products?cafeId=${encodeURIComponent(clientId)}`, { method: "GET" });
    const products = Array.isArray(prodRes.data) ? prodRes.data : prodRes.data?.data || [];
    const firstId = products[0]?.id;
    if (!firstId) {
      return savePersonal({ clientId: Number(clientId) });
    }

    const detailRes = await api(`/products/${firstId}/detail`, { method: "GET" });
    const profile = clientFromPayload(detailRes.data, {
      clientId: Number(clientId),
    });
    return savePersonal(profile);
  } catch (err) {
    console.warn("Gagal ambil profil client", err);
    return personal;
  }
}

export function getKeranjang() {
  try {
    const list = JSON.parse(localStorage.getItem("keranjang") || "[]");
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

export function mapPaymentMethod(raw) {
  const type = String(raw || "").toLowerCase();
  const qrisTypes = ["qris", "gopay", "shopeepay", "dana", "ovo", "linkaja", "other_qris"];
  const bankTypes = [
    "bank",
    "bank_transfer",
    "echannel",
    "bca_klikpay",
    "bca_klikbca",
    "bri_epay",
    "cimb_clicks",
    "credit_card",
    "cstore",
  ];
  if (qrisTypes.includes(type)) return "qris";
  if (bankTypes.includes(type)) return "bank";
  if (type === "cash") return "cash";
  return "qris";
}

export function formatPesananError(err) {
  const d = err?.response?.data;
  if (d == null) return err?.message || "Gagal membuat pesanan";
  if (typeof d === "string") return d.slice(0, 500);
  if (d.errors && typeof d.errors === "object") {
    const k = Object.keys(d.errors)[0];
    if (k) {
      const v = d.errors[k];
      return Array.isArray(v) ? v[0] : String(v);
    }
  }
  if (typeof d.message === "string" && d.message) return d.message;
  return err?.message || "Gagal membuat pesanan";
}

export function buildPesananBody({ method, result } = {}) {
  const personal = getPersonal();
  const keranjangs = getKeranjang();
  // Kirim identifier mentah. BE sudah resolve uniqId / table_code / nama meja.
  // Jangan ubah nomor meja jadi Number() — itu bisa jadi id_table yang salah dan 422.
  const noCafe = personal?.clientToken || personal?.clientId || null;
  const noMeja =
    personal?.meja != null && String(personal.meja).trim() !== ""
      ? String(personal.meja).trim()
      : personal?.tableId ?? null;
  const nama_order =
    (personal && (personal.nama_order || personal.nama || personal.name)) || "Guest";
  const payMethod = mapPaymentMethod(
    method || result?.payment_type || localStorage.getItem("midtrans_payment_method") || "qris"
  );

  const invalidDetails = keranjangs.filter((k) => !Number(k.product_id || k.id));
  const cash = keranjangs.reduce((sum, k) => {
    const harga = Number(k.harga) || 0;
    const qty = Number(k.jumlah_pemesanan || 1) || 1;
    return sum + harga * qty;
  }, 0);

  return {
    personal,
    keranjangs,
    invalidDetails,
    noCafe,
    noMeja,
    body: {
      noMeja,
      noCafe,
      nama_order,
      method: payMethod,
      cash,
      details: keranjangs.map((k) => ({
        product: Number(k.product_id || k.id),
        jumlah: Number(k.jumlah_pemesanan || 1),
        note: k.note && String(k.note).trim() ? String(k.note).trim() : null,
      })),
    },
  };
}

export function getMidtransOrderId() {
  return localStorage.getItem("midtrans_order_id") || "";
}

export function clearPaidCart() {
  localStorage.removeItem("keranjang");
  localStorage.removeItem("midtrans_payment_method");
  localStorage.removeItem("midtrans_payment_status");
  localStorage.removeItem("midtrans_payment_raw");
  localStorage.removeItem("midtrans_order_id");
  localStorage.removeItem("midtrans_order_saved");
  localStorage.removeItem("midtrans_should_save");
}

export async function submitPesanan({ method, result, orderId } = {}) {
  const packed = buildPesananBody({ method, result });
  const order_id = orderId || result?.order_id || getMidtransOrderId() || "";

  if (!order_id && !packed.keranjangs.length) {
    throw new Error("Keranjang kosong, pesanan tidak dikirim.");
  }
  if (!order_id) {
    if (!packed.noCafe) {
      throw new Error("Client tidak ditemukan. Buka ulang dari link cafe/meja.");
    }
    if (packed.noMeja == null || packed.noMeja === "") {
      throw new Error("Nomor meja tidak ditemukan. Buka ulang dari link meja.");
    }
    if (packed.invalidDetails.length) {
      throw new Error("Ada item keranjang tanpa ID produk. Hapus lalu pesan ulang.");
    }
  }

  const payload = {
    ...packed.body,
    order_id,
    transaction_status: result?.transaction_status || localStorage.getItem("midtrans_payment_status") || null,
    payment_type: result?.payment_type || method || localStorage.getItem("midtrans_payment_method") || packed.body.method,
  };

  try {
    if (order_id) {
      const res = await api("/midtrans/confirm", {
        method: "POST",
        data: payload,
      });
      if (res.data && res.data.saved === false) {
        const err = new Error(res.data.message || "Pembayaran belum lunas.");
        err.response = { data: res.data };
        throw err;
      }
      clearPaidCart();
      return res.data;
    }

    const res = await api("/pesanans/post", {
      method: "POST",
      data: packed.body,
    });
    clearPaidCart();
    return res.data;
  } catch (err) {
    console.error("Simpan pesanan gagal", payload, err?.response?.data || err);
    throw err;
  }
}
